import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../api'
import { formatDate } from '../session'

// The bell in the top bar: your unread count, and the last 50 notices.
// Opening one marks it read and goes to what it's about.
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const inbox = useQuery({ queryKey: ['notifications'], queryFn: api.notifications, refetchInterval: 60_000 })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] })
  const markRead = useMutation({ mutationFn: api.markNotificationRead, onSuccess: refresh })
  const markAll = useMutation({ mutationFn: api.markAllNotificationsRead, onSuccess: refresh })

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => panel.current && !panel.current.contains(event.target as Node) && setOpen(false)
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const unread = inbox.data?.unreadCount ?? 0

  return (
    <div ref={panel} style={{ position: 'relative', marginLeft: 'auto' }}>
      <button type="button" className="btn btn-quiet" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={`Notifications, ${unread} unread`}>
        <Bell size={18} aria-hidden />
        {unread > 0 && (
          <span className="pill pill-brand" style={{ padding: '0 7px' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="card" role="dialog" aria-label="Notifications" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 'min(380px, 90vw)', maxHeight: 460, overflowY: 'auto', zIndex: 10 }}>
          <div className="card-head">
            <h2>Notifications</h2>
            {unread > 0 && (
              <button type="button" className="btn btn-quiet" onClick={() => markAll.mutate()}>
                Mark all read
              </button>
            )}
          </div>
          {inbox.data?.notifications.length === 0 && <div className="empty">Nothing yet.</div>}
          <ul className="list">
            {inbox.data?.notifications.map((n) => (
              <li key={n.id} style={{ alignItems: 'flex-start' }}>
                <button
                  type="button"
                  className="btn btn-quiet"
                  style={{ textAlign: 'left', padding: 0, display: 'block', width: '100%', fontWeight: n.readAt ? 400 : 600, color: 'var(--ink)' }}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id)
                    setOpen(false)
                    if (n.link) navigate(n.link)
                  }}
                >
                  {n.title}
                  {n.body && <div className="faint" style={{ fontWeight: 400 }}>{n.body}</div>}
                  <div className="faint" style={{ fontWeight: 400 }}>{formatDate(n.createdAt, true)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
